import { Minus, Plus, ChevronDown } from 'lucide-react';
import { useRef, useCallback, useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface NumberStepperProps {
  value: number | null;
  onChange: (value: number | null) => void;
  min: number;
  max?: number;
  step: number;
  label?: string;
  unit?: string;
  className?: string;
  presets?: number[];  // Pikavalinta-arvot (esim. [10, 15, 20, 25, ...])
}

export function NumberStepper({
  value,
  onChange,
  min,
  max = 9999,
  step,
  label,
  unit,
  className,
  presets,
}: NumberStepperProps) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showPresets, setShowPresets] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const presetsRef = useRef<HTMLDivElement>(null);

  const currentValue = value ?? min;

  const increment = useCallback(() => {
    const newValue = Math.min(max, Math.round((currentValue + step) * 100) / 100);
    onChange(newValue);
  }, [currentValue, max, step, onChange]);

  const decrement = useCallback(() => {
    const newValue = Math.max(min, Math.round((currentValue - step) * 100) / 100);
    onChange(newValue);
  }, [currentValue, min, step, onChange]);

  // Estä tuplaklikkaustapahtumat (touch + mouse)
  const isActiveRef = useRef(false);

  // Pitkä painallus -toiminto
  const startRepeating = useCallback((action: () => void, e: React.PointerEvent) => {
    // Estä tuplaklikkaustapahtumat
    if (isActiveRef.current) return;
    isActiveRef.current = true;

    // Estä selaimen oletustoiminnot (esim. teksin valinta, context menu)
    e.preventDefault();

    action(); // Ensimmäinen klikkaus heti

    // Aloita toisto 400ms viiveellä
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(action, 100);
    }, 400);
  }, []);

  const stopRepeating = useCallback(() => {
    isActiveRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Käsittele suora syöttö
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValue = e.target.value;
    setInputValue(newInputValue);

    if (newInputValue === '') {
      onChange(null);
      return;
    }

    // Salli kirjoittaminen vapaasti, parsi vasta lopuksi
    const parsed = parseFloat(newInputValue.replace(',', '.'));
    if (!isNaN(parsed)) {
      const clamped = Math.max(min, Math.min(max, parsed));
      onChange(clamped);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    // Aseta inputValue nykyiseen arvoon
    setInputValue(value !== null ? value.toString() : '');
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    setInputValue('');
  };

  const getDisplayValue = (): string => {
    // Jos kenttä on fokuksessa, näytä käyttäjän kirjoittama arvo
    if (isFocused) {
      return inputValue;
    }
    // Muuten näytä formatoitu arvo
    if (value === null) return '';
    if (step < 1) {
      return value.toFixed(1);
    }
    return value.toString();
  };

  const handlePresetSelect = (preset: number) => {
    onChange(preset);
    setShowPresets(false);
  };

  // Sulje preset-valikko kun klikataan muualle
  useEffect(() => {
    if (!showPresets) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (presetsRef.current && !presetsRef.current.contains(e.target as Node)) {
        setShowPresets(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPresets]);

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && (
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {label}
        </label>
      )}
      <div className="flex items-center">
        {/* Vähennä-nappi */}
        <button
          type="button"
          onPointerDown={(e) => startRepeating(decrement, e)}
          onPointerUp={stopRepeating}
          onPointerLeave={stopRepeating}
          onPointerCancel={stopRepeating}
          disabled={currentValue <= min}
          className={cn(
            'flex items-center justify-center w-9 h-9 shrink-0 rounded-l-lg',
            'bg-gray-100 dark:bg-gray-700 border border-r-0 border-gray-300 dark:border-gray-600',
            'text-gray-700 dark:text-gray-300',
            'hover:bg-gray-200 dark:hover:bg-gray-600 active:bg-gray-300 dark:active:bg-gray-500',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors select-none touch-manipulation'
          )}
        >
          <Minus className="h-4 w-4" />
        </button>

        {/* Arvo */}
        <div className="relative flex-1 min-w-[40px]">
          <input
            type="text"
            inputMode="decimal"
            value={getDisplayValue()}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            className={cn(
              'w-full h-9 text-center text-sm font-medium',
              'bg-white dark:bg-gray-800 border-y border-gray-300 dark:border-gray-600',
              'text-gray-900 dark:text-white',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
            )}
          />
        </div>

        {/* Lisää-nappi */}
        <button
          type="button"
          onPointerDown={(e) => startRepeating(increment, e)}
          onPointerUp={stopRepeating}
          onPointerLeave={stopRepeating}
          onPointerCancel={stopRepeating}
          disabled={currentValue >= max}
          className={cn(
            'flex items-center justify-center w-9 h-9 shrink-0 rounded-r-lg',
            'bg-gray-100 dark:bg-gray-700 border border-l-0 border-gray-300 dark:border-gray-600',
            'text-gray-700 dark:text-gray-300',
            'hover:bg-gray-200 dark:hover:bg-gray-600 active:bg-gray-300 dark:active:bg-gray-500',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors select-none touch-manipulation'
          )}
        >
          <Plus className="h-4 w-4" />
        </button>

        {/* Yksikkö erikseen napin jälkeen */}
        {unit && (
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1 shrink-0">
            {unit}
          </span>
        )}
      </div>

      {/* Pikavalinta - erillinen rivi */}
      {presets && presets.length > 0 && (
        <div className="relative" ref={presetsRef}>
          <button
            type="button"
            onClick={() => setShowPresets(!showPresets)}
            className={cn(
              'w-full flex items-center justify-center gap-1 px-2 py-1 rounded-sm',
              'bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800',
              'text-primary-700 dark:text-primary-300 text-xs font-medium',
              'hover:bg-primary-100 dark:hover:bg-primary-900/50',
              'transition-colors'
            )}
          >
            <span>Valitse {unit || ''}</span>
            <ChevronDown className={cn('h-3 w-3 transition-transform', showPresets && 'rotate-180')} />
          </button>

          {/* Dropdown-valikko - yksi sarake */}
          {showPresets && (
            <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {presets.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => handlePresetSelect(preset)}
                  className={cn(
                    'w-full px-3 py-2 text-sm text-left border-b border-gray-100 dark:border-gray-700 last:border-0',
                    'hover:bg-gray-100 dark:hover:bg-gray-700',
                    'text-gray-900 dark:text-white',
                    value === preset && 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 font-medium'
                  )}
                >
                  {preset} {unit}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
